import { Input as BaseInput } from '@base-ui-components/react/input';
import { forwardRef } from 'react';
import { cn } from '../cn.js';

export interface InputProps
  // The native `size` is a character count; the design system's is a control height.
  extends Omit<React.ComponentPropsWithoutRef<typeof BaseInput>, 'size'> {
  /** Marks the field invalid and switches the border to the danger token. */
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const heightBySize = {
  sm: 'h-(--input-height-sm) text-(length:--font-size-caption)',
  md: 'h-(--input-height-md) text-(length:--font-size-body)',
  lg: 'h-(--input-height-lg) text-(length:--font-size-body-large)',
} as const;

/**
 * A single-line text field. The label, description, and error live in FormField, which links them
 * with `aria-describedby`; this component owns only the control itself.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, size = 'md', ...props },
  ref,
) {
  return (
    <BaseInput
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-(--input-radius) px-(--input-padding-inline)',
        'bg-(--color-surface) text-(--color-text-primary)',
        'border border-(--input-border-color)',
        'placeholder:text-(--input-placeholder)',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
        'disabled:opacity-(--opacity-disabled) disabled:cursor-not-allowed',
        'read-only:bg-(--color-surface-sunken)',
        heightBySize[size],
        invalid && 'border-(--input-border-color-invalid)',
        className,
      )}
      {...props}
    />
  );
});
